import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useNavigate } from 'react-router';
import { MembershipTier } from 'types/SignUpData';

const getTierInfo = (theme: Theme): Record<
  MembershipTier,
  { label: string; color: string }
> => ({
  'Tier 1': { label: 'Tier 1', color: theme.palette.primary.main },
  'Tier 2': { label: 'Tier 2', color: theme.palette.secondary.light },
  'Tier 3': { label: 'Tier 3', color: theme.palette.primary.dark },
});


export interface TierGateDialogProps {
  open: boolean;
  onClose: () => void;
  currentTier?: MembershipTier;
  requiredTier: MembershipTier;
  featureLabel: string;
}


export default function TierGateDialog({
  open,
  onClose,
  currentTier,
  requiredTier,
  featureLabel,
}: TierGateDialogProps): React.ReactElement {
  const theme = useTheme();
  const navigate = useNavigate();

  const TIER_INFO = getTierInfo(theme);
  const required = TIER_INFO[requiredTier];
  const current = currentTier ? TIER_INFO[currentTier] : null;

  const handleUpgrade = (): void => {
    onClose();
    navigate('/profile');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
        }}
      >
        <LockIcon sx={{ color: required.color }} />
        Feature Unavailable
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>{featureLabel}</strong> requires a higher membership tier.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          {current && (
            <Chip
              label={`Your tier: ${current.label}`}
              variant="outlined"
              size="small"
              sx={{ borderColor: current.color, color: current.color }}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            →
          </Typography>
          <Chip
            label={`Required: ${required.label}`}
            size="small"
            sx={{ bgcolor: required.color, color: '#fff', fontWeight: 'bold' }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary">
          You can request a tier upgrade from your Profile page. An
          administrator will review your request.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Dismiss</Button>
        <Button
          variant="contained"
          startIcon={<ArrowUpwardIcon />}
          onClick={handleUpgrade}
          sx={{
            bgcolor: required.color,
            '&:hover': { bgcolor: required.color, filter: 'brightness(0.9)' },
          }}
        >
          Request Upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
}
